import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import type { OAuthAuthorizationDetails } from '@supabase/supabase-js'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import pronteerLogoFull from '@/assets/pronteer-logo-full.svg'
import { Button } from '@/components/ui/button'
import { Loading } from '@/components/ui/loading'

// OAuth consent for third-party AI assistants (e.g. a Claude.ai custom
// connector) reading Pronteer through the API's read-only MCP endpoint
// (apps/api/src/mcp). Supabase Auth's OAuth 2.1 server sends the browser here —
// Site URL + Authorization Path "/oauth/consent" — with ?authorization_id=…;
// approving hands the assistant a token for the signed-in user.

// Survives the /signin detour, which only carries a path in `redirect`.
const PENDING_AUTHORIZATION_KEY = 'pronteer.oauth.authorizationId'

type ConsentSearch = { authorization_id?: string }

export const Route = createFileRoute('/oauth/consent')({
  component: OAuthConsentRoute,
  validateSearch: (search: Record<string, unknown>): ConsentSearch => ({
    authorization_id: search.authorization_id as string | undefined,
  }),
})

const READ_SCOPES = [
  'Your schedule, roster and upcoming trips',
  'Your document expiry dates',
  'Your tasks and pending flight logs',
  'Company announcements and your notifications',
]

function readPendingId(): string | null {
  try {
    return sessionStorage.getItem(PENDING_AUTHORIZATION_KEY)
  } catch {
    return null
  }
}

function writePendingId(id: string | null) {
  try {
    if (id) sessionStorage.setItem(PENDING_AUTHORIZATION_KEY, id)
    else sessionStorage.removeItem(PENDING_AUTHORIZATION_KEY)
  } catch {
    // Private mode / blocked storage: the user just restarts from the assistant.
  }
}

function OAuthConsentRoute() {
  const { isLoaded, isSignedIn } = useAuth()
  const { authorization_id: searchId } = Route.useSearch()
  const authorizationId = searchId ?? readPendingId()

  const [details, setDetails] = useState<OAuthAuthorizationDetails | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (authorizationId) writePendingId(authorizationId)
  }, [authorizationId])

  // Signed out: sign in first, then /signin's redirect brings them back here
  // (the authorization id waits in sessionStorage). This page only ever runs
  // in a browser, so a plain URL redirect is fine.
  useEffect(() => {
    if (isLoaded && !isSignedIn && authorizationId) {
      window.location.replace(`/signin?redirect=${encodeURIComponent('/oauth/consent')}`)
    }
  }, [isLoaded, isSignedIn, authorizationId])

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !authorizationId) return
    let cancelled = false
    void (async () => {
      const { data, error } = await supabase.auth.oauth.getAuthorizationDetails(authorizationId)
      if (cancelled) return
      if (error || !data) {
        writePendingId(null)
        setError(error?.message ?? 'This authorization request is invalid or has expired.')
        return
      }
      // Already approved earlier: Supabase returns only where to send the user.
      if (!('authorization_id' in data)) {
        writePendingId(null)
        window.location.href = data.redirect_url
        return
      }
      setDetails(data)
    })()
    return () => {
      cancelled = true
    }
  }, [isLoaded, isSignedIn, authorizationId])

  const decide = async (approve: boolean) => {
    if (!authorizationId) return
    setSubmitting(true)
    const { data, error } = approve
      ? await supabase.auth.oauth.approveAuthorization(authorizationId)
      : await supabase.auth.oauth.denyAuthorization(authorizationId)
    writePendingId(null)
    if (error || !data) {
      setSubmitting(false)
      setError(error?.message ?? 'Something went wrong. Please start again from your assistant.')
      return
    }
    window.location.href = data.redirect_url
  }

  if (!isLoaded) return <Centered><Loading size={48} /></Centered>
  if (!authorizationId) {
    return <Centered><Message text="This link is missing its authorization request. Start the connection again from your assistant." /></Centered>
  }
  if (!isSignedIn) return <Centered><Loading size={48} /></Centered>
  if (error) return <Centered><Message text={error} /></Centered>
  if (!details) return <Centered><Loading size={48} /></Centered>

  const clientName = details.client.name || 'An application'

  return (
    <div className="flex-1 bg-surface-L0 flex flex-col justify-center gap-10 overflow-y-auto px-4 pt-[calc(var(--safe-top)+24px)] pb-[calc(var(--safe-bottom)+16px)]">
      <img src={pronteerLogoFull} alt="Pronteer" className="h-[46px] w-[143px] mx-auto" />

      <div className="w-full max-w-[361px] mx-auto flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-[24px] font-semibold text-content-brand-bold leading-tight">
            Connect {clientName} to Pronteer
          </h1>
          <p className="text-sm text-content-primary-bold leading-5">
            {clientName} is asking for read-only access to your Pronteer account
            {details.user.email ? ` (${details.user.email})` : ''}.
          </p>
        </div>

        <div className="flex flex-col gap-3 rounded-lg border border-border-L2 p-4">
          <p className="text-xs font-medium text-content-primary-subtle">It will be able to read</p>
          <ul className="flex flex-col gap-2">
            {READ_SCOPES.map((scope) => (
              <li key={scope} className="text-sm text-content-primary-bold leading-5">
                • {scope}
              </li>
            ))}
          </ul>
          <p className="text-xs text-content-primary-subtle leading-4">
            It can't create, change or delete anything.
          </p>
        </div>

        <p className="text-xs text-content-primary-subtle leading-4 break-all">
          You'll be sent back to {details.redirect_uri}
        </p>

        <div className="flex flex-col gap-3">
          <Button
            type="button"
            onClick={() => decide(true)}
            disabled={submitting}
            className="w-full h-[45px] rounded-lg bg-background-brand-bold hover:bg-background-brand-bold/90 active:bg-background-brand-bold/80 text-content-primary-inverse text-sm font-semibold"
          >
            {submitting ? 'Connecting…' : 'Allow read-only access'}
          </Button>
          <button
            type="button"
            onClick={() => decide(false)}
            disabled={submitting}
            className="text-xs text-content-primary-subtle text-center underline"
          >
            Deny
          </button>
        </div>
      </div>
    </div>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex h-full flex-1 items-center justify-center p-6">{children}</div>
}

function Message({ text }: { text: string }) {
  return <p className="max-w-[361px] text-center text-sm text-content-primary-subtle">{text}</p>
}
